import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Box, Card, List, ListItemButton, ListItemText, Breadcrumbs, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { axiosInstance } from "../component/Api/instance";
import { UserContext } from "../Context";
import { PrimaryButton } from "../component/ui/Buttons";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MicroTestIcon from "../asserts/exam_8980068.jpg";
import CrownIcon from "../asserts/crown.png";

const InstructionPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "24px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
}));

const InstructionDetailsCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "40px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  minHeight: "450px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    padding: "24px",
  },
}));

const PaperItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected'
})(({ theme, selected }) => ({
  borderRadius: "14px",
  marginBottom: "8px",
  padding: "12px 16px",
  backgroundColor: selected ? `${theme.palette.primary.main}0D` : "transparent",
  border: `1px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: selected ? 700 : 500,
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: selected ? `${theme.palette.primary.main}1A` : theme.palette.action.hover,
  },
}));

export default function DailyInstruction() {
  const navigate = useNavigate();
  const [options, setOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [paperid, setpaperid] = useState(null);
  const email = localStorage.getItem("userMail");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.post(
          `get/U_ViewDailyTestDetails.php`,
          {
            userId: email,
          }
        );
        if (response.status === 200) {
          if (response.data.message === "timeout") {
            navigate("/signin");
          }
          const data = response.data || [];
          setOptions(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [email, navigate]);

  const PaperData = async (ind) => {
    setSelectedIndex(ind);
    setpaperid(options[ind].sno);

    try {
      await startTest(options[ind].sno);
    } catch (error) {
      console.error("Error starting test:", error);
    }
  };

  const startTest = async (testId) => {
    try {
      const response = await axiosInstance.post(`post/U_DailyTest.php`, {
        id: email,
        test_id: testId,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
        }
        console.log("Test started:", response.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleStartExamClick = () => {
    if (selectedIndex !== null) {
      navigate(`/testpage/${options[selectedIndex].sno}`);
    }
  };

  const instructions = [
    "There are a total of 10 questions.",
    "Each question carries 1 mark and there are no negative marks.",
    "Mark your answer by clicking the appropriate radio button and to change your answer, please click the radio button that you think is appropriate.",
    "Once done, click on finish test to end the test.",
    "Click on start to proceed."
  ];

  return (
    <InstructionPageContainer>
      <Container>
        <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: "24px" }}>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/home"); }}>
            Home
          </Link>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/hybrid"); }}>
            Hybrid Test
          </Link>
          <Typography color="text.primary">Daily Test Instructions</Typography>
        </Breadcrumbs>

        <Box>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
            <Box style={{ maxWidth: "750px" }}>
              <Typography variant="h1" style={{ fontWeight: 800 }}>
                Daily Test
              </Typography>
              <Typography variant="body1" color="textSecondary" style={{ marginTop: "8px", lineHeight: 1.6 }}>
                Hybrid test is a platform for candidates to use the time effectively. Daily test questions are updated at 9am in the morning.
              </Typography>
            </Box>
            <img
              src={MicroTestIcon}
              alt="Daily Test"
              style={{ maxHeight: "65px", objectFit: "contain", borderRadius: "12px" }}
            />
          </Box>

          <Row className="g-4">
            <Col xs={12} md={4} lg={3}>
              <SidebarCard>
                <Box style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", color: "var(--color-primary)" }}>
                  <CalendarMonthIcon />
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    Select Exam Paper
                  </Typography>
                </Box>

                {options.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No paper options found.
                  </Typography>
                ) : (
                  <List style={{ padding: 0 }}>
                    {options.map((option, index) => (
                      <PaperItemButton
                        key={index}
                        selected={selectedIndex === index}
                        onClick={() => PaperData(index)}
                      >
                        <ListItemText 
                          primary={
                            <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                              <Typography style={{ fontWeight: selectedIndex === index ? 700 : 500, fontSize: "0.9rem" }}>
                                {option.test_name}
                              </Typography>
                              {option.category === "premium" && (
                                <img src={CrownIcon} height="16px" alt="Crown" />
                              )}
                            </Box>
                          }
                        />
                      </PaperItemButton>
                    ))}
                  </List>
                )}
              </SidebarCard>
            </Col>

            <Col xs={12} md={8} lg={9}>
              <InstructionDetailsCard>
                <Box>
                  <Box style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "24px" }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h2" style={{ fontWeight: 800 }}>
                      Candidate Instructions
                    </Typography>
                  </Box>

                  <Box style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    {instructions.map((instruction, index) => (
                      <Box key={index} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "50%", backgroundColor: "rgba(30, 58, 138, 0.08)", color: "var(--color-primary)", fontWeight: 700, fontSize: "0.75rem", marginTop: "2px" }}>
                          {index + 1}
                        </Box>
                        <Typography variant="body1" color="textPrimary" style={{ lineHeight: 1.6 }}>
                          {instruction}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: "40px" }}>
                  {selectedIndex !== null ? (
                    <PrimaryButton
                      onClick={handleStartExamClick}
                      endIcon={<ArrowForwardIcon />}
                      style={{ height: "46px", borderRadius: "14px", minWidth: "150px" }}
                      disabled={!paperid}
                    >
                      Start Exam
                    </PrimaryButton>
                  ) : (
                    <Box style={{ display: "flex", alignItems: "center", gap: "8px", color: "var(--color-text-secondary)" }}>
                      <HelpOutlineIcon fontSize="small" />
                      <Typography variant="body2">
                        Please select an exam paper from the left panel to begin.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </InstructionDetailsCard>
            </Col>
          </Row>
        </Box>
      </Container>
    </InstructionPageContainer>
  );
}
